import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getRunStatus, resumeAnalysis } from '../api/client';

export default function AnalysisProgress() {
  const { id, runId } = useParams();
  const navigate      = useNavigate();
  const [status, setStatus]     = useState(null);
  const [resuming, setResuming] = useState(false);
  const interval = useRef(null);

  function startPolling() {
    clearInterval(interval.current);
    interval.current = setInterval(poll, 2000);
  }

  async function poll() {
    try {
      const s = await getRunStatus(runId);
      setStatus(s);
      if (s.status === 'complete') {
        clearInterval(interval.current);
        setTimeout(() => navigate(`/reviews/${id}/results`), 1800);
      } else if (s.status === 'failed' || s.status === 'paused') {
        clearInterval(interval.current);
      }
    } catch (e) {
      console.error('Poll error', e);
    }
  }

  async function handleContinue() {
    setResuming(true);
    try {
      await resumeAnalysis(runId);
      setStatus(s => ({ ...s, status: 'running', pause_reason: null }));
      startPolling();
    } catch (e) {
      console.error('Resume error', e);
    } finally {
      setResuming(false);
    }
  }

  useEffect(() => {
    poll();
    startPolling();
    return () => clearInterval(interval.current);
  }, [runId, id, navigate]);

  if (!status) return (
    <div className="page-narrow" style={{ textAlign: 'center', paddingTop: 100 }}>
      <p className="text-muted">Starting...</p>
    </div>
  );

  const isDone   = status.status === 'complete';
  const isFailed = status.status === 'failed';
  const isPaused = status.status === 'paused';
  const stages   = status.stages || [];

  return (
    <div className="page-narrow">
      {/* Header */}
      <motion.div
        style={{ textAlign: 'center', marginBottom: 48 }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 style={{
          fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.03em',
          color: isDone ? 'var(--green)' : isFailed ? 'var(--red)'
                 : isPaused ? '#a96f10' : '#1A1A2E',
          marginBottom: 8, transition: 'color 0.4s',
        }}>
          {isDone ? '✓ Analysis Complete' : isFailed ? '✗ Analysis Failed'
            : isPaused ? '⏸ Analysis Paused' : 'Analysing...'}
        </h1>
        <p style={{ color: '#6B7280', fontSize: '0.9rem', fontStyle: 'italic' }}>
          {!isDone && !isFailed && !isPaused && (status.current_action || 'Processing...')}
          {isPaused && 'Action needed to continue'}
          {isDone && 'Redirecting to results...'}
        </p>
      </motion.div>

      {/* Paused — reason + Continue */}
      {isPaused && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            marginBottom: 28, background: 'rgba(216,148,31,0.08)',
            border: '1px solid rgba(216,148,31,0.30)', borderRadius: 12, padding: '20px 22px',
          }}
        >
          <div style={{ fontWeight: 600, color: '#a96f10', marginBottom: 6 }}>
            Why this paused
          </div>
          <p style={{ color: '#5b4a23', fontSize: '0.92rem', lineHeight: 1.55, margin: 0 }}>
            {status.pause_reason || 'The analysis was interrupted before it finished.'}
          </p>
          <p style={{ color: '#8a7a4d', fontSize: '0.8rem', margin: '10px 0 16px' }}>
            Your progress so far is saved — continuing will pick up exactly where it stopped.
          </p>
          <button
            onClick={handleContinue}
            disabled={resuming}
            style={{
              background: '#a96f10', color: '#fff', border: 'none', borderRadius: 8,
              padding: '11px 22px', fontSize: '0.92rem', fontWeight: 600,
              cursor: resuming ? 'default' : 'pointer', opacity: resuming ? 0.6 : 1,
            }}
          >
            {resuming ? 'Continuing…' : 'Continue analysis'}
          </button>
        </motion.div>
      )}

      {/* Live counters */}
      {!isFailed && (
        <motion.div
          className="stats-row"
          style={{ marginBottom: 36 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {[
            { cls: '',      value: `${status.documents_parsed || 0}/${status.total_documents || 0}`, label: 'Documents' },
            { cls: 'red',   value: status.total_findings_red   || 0, label: 'Red' },
            { cls: 'amber', value: status.total_findings_amber || 0, label: 'Amber' },
            { cls: 'green', value: status.total_findings_green || 0, label: 'Clear' },
          ].map((s, i) => (
            <div key={i} className={`stat-card ${s.cls}`}>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Step tracker */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
        style={{ padding: '32px 36px' }}
      >
        <div style={{ position: 'relative' }}>
          {stages.map((stage, i) => {
            const isLast     = i === stages.length - 1;
            const isComplete = stage.status === 'complete';
            const isActive   = stage.status === 'in_progress';
            const nextFilled = !isLast && stages[i + 1]?.status !== 'waiting';

            return (
              <div key={i} style={{ display: 'flex', gap: 20, position: 'relative' }}>
                {/* Vine connector */}
                {!isLast && (
                  <div style={{
                    position: 'absolute', left: 15, top: 32,
                    width: 2, height: 'calc(100% - 4px)',
                    background: nextFilled ? 'var(--navy)' : '#E5E7EB',
                    transition: 'background 0.6s ease',
                    zIndex: 0,
                  }} />
                )}

                {/* Step icon */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isComplete ? 16 : 13, fontWeight: 600,
                  background: isComplete ? 'var(--green)' : isActive ? 'var(--navy)' : '#F3F4F6',
                  color: (isComplete || isActive) ? '#fff' : '#9CA3AF',
                  border: isActive ? 'none' : isComplete ? 'none' : '2px solid #E5E7EB',
                  animation: isActive ? 'pulse-ring 1.5s infinite' : 'none',
                  transition: 'background 0.4s ease, color 0.4s ease',
                }}>
                  {isComplete ? '✓' : isActive ? '◉' : i + 1}
                </div>

                {/* Step content */}
                <div style={{ paddingBottom: isLast ? 0 : 28, paddingTop: 4, flex: 1 }}>
                  <div style={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.95rem',
                    color: isComplete ? '#9CA3AF' : isActive ? 'var(--navy)' : '#D1D5DB',
                    transition: 'color 0.4s ease, font-weight 0.3s',
                  }}>
                    {stage.name}
                  </div>
                  {isActive && (
                    <>
                      <div style={{
                        marginTop: 10, height: 6, background: '#F3F4F6',
                        borderRadius: 3, overflow: 'hidden', width: '100%',
                      }}>
                        <motion.div
                          style={{ height: '100%', background: 'var(--navy)', borderRadius: 3 }}
                          initial={{ width: 0 }}
                          animate={{ width: `${stage.progress}%` }}
                          transition={{ duration: 0.4 }}
                        />
                      </div>
                      {status.total_in_stage > 1 && (
                        <div style={{
                          marginTop: 6, fontSize: '0.8rem', fontWeight: 500, color: '#6B7280',
                        }}>
                          {Math.min(status.progress_in_stage, status.total_in_stage)} of {status.total_in_stage}{' '}
                          {status.current_stage === 3 ? 'documents analysed' : 'documents processed'}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Error */}
      {isFailed && (
        <div style={{
          marginTop: 16, background: 'rgba(231,76,60,0.06)', color: 'var(--red)',
          border: '1px solid rgba(231,76,60,0.2)', borderRadius: 12, padding: '16px 20px',
        }}>
          <strong>Error:</strong> {status.error_message || 'Unknown error'}
        </div>
      )}

      {/* Cost */}
      {status.api_cost_usd > 0 && (
        <p className="text-muted text-sm" style={{ textAlign: 'center', marginTop: 20 }}>
          API cost so far: ${status.api_cost_usd.toFixed(4)}
        </p>
      )}
    </div>
  );
}
